"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { Button } from "@/components/ui/button";
import { MailCheck } from "lucide-react";

type Mode = "magic" | "password" | "signup";

export function AuthCard() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<Mode>("magic");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await signIn("email", { email, callbackUrl: "/", redirect: false });
      if (result?.error) {
        setError("Failed to send login link. Please try again.");
      } else {
        setSent(true);
      }
    } catch {
      setError("Failed to send login link. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordSignIn(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await signIn("credentials", { email, password, callbackUrl: "/app", redirect: false });
      if (result?.error) {
        setError("Invalid email or password.");
      } else if (result?.url) {
        window.location.href = result.url;
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Sign up failed.");
        return;
      }
      const result = await signIn("credentials", { email, password, callbackUrl: "/app", redirect: false });
      if (result?.error) {
        setError("Account created — please sign in.");
        setMode("password");
      } else if (result?.url) {
        window.location.href = result.url;
      }
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div className="text-center py-4">
        <div className="flex justify-center mb-3"><MailCheck className="size-8 text-[#2a9695]" /></div>
        <p className="font-semibold text-gray-900 mb-1">Check your email</p>
        <p className="text-sm text-gray-500">We sent a sign-in link to <strong>{email}</strong></p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Google — primary */}
      <button
        type="button"
        onClick={() => signIn("google", { callbackUrl: "/" })}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <svg className="size-4" viewBox="0 0 24 24">
          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        Continue with Google
      </button>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Email form */}
      <form
        onSubmit={
          mode === "magic" ? handleMagicLink :
          mode === "signup" ? handleSignUp :
          handlePasswordSignIn
        }
        className="space-y-3"
      >
        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(null); }}
          required
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1c3d51] focus:border-transparent"
        />

        {(mode === "password" || mode === "signup") && (
          <input
            type="password"
            placeholder={mode === "signup" ? "Create a password (min 8 chars)" : "Password"}
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
            required
            minLength={8}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1c3d51] focus:border-transparent"
          />
        )}

        {error && <p className="text-xs text-red-800">{error}</p>}

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#1c3d51] hover:bg-[#1F5867] text-white"
        >
          {loading ? "Please wait…" :
            mode === "magic" ? "Send login link" :
            mode === "signup" ? "Create account" :
            "Sign in"}
        </Button>
      </form>

      {/* Mode toggles */}
      <div className="flex justify-between text-xs text-gray-500">
        {mode === "magic" && (
          <>
            <button type="button" onClick={() => { setMode("password"); setError(null); }} className="hover:text-gray-800 underline underline-offset-2">
              Use password instead
            </button>
            <button type="button" onClick={() => { setMode("signup"); setError(null); }} className="hover:text-gray-800 underline underline-offset-2">
              Sign up with password
            </button>
          </>
        )}
        {mode === "password" && (
          <>
            <button type="button" onClick={() => { setMode("magic"); setError(null); }} className="hover:text-gray-800 underline underline-offset-2">
              Use login link instead
            </button>
            <button type="button" onClick={() => { setMode("signup"); setError(null); }} className="hover:text-gray-800 underline underline-offset-2">
              Create an account
            </button>
          </>
        )}
        {mode === "signup" && (
          <button type="button" onClick={() => { setMode("password"); setError(null); }} className="hover:text-gray-800 underline underline-offset-2">
            Already have an account? Sign in
          </button>
        )}
      </div>
    </div>
  );
}
